import React from 'react';
import { Card, Row, Col, Statistic, Typography, Button, Space, Progress, Tag } from 'antd';
import { ShoppingOutlined, DollarOutlined } from '@ant-design/icons';
import { useQuery } from 'react-query';
import api from '../services/api';
import { useAuthStore } from '../stores/authStore';
import { useNavigate } from 'react-router-dom';

const { Title, Text } = Typography;

const Dashboard = () => {
  const { user } = useAuthStore();
  const navigate = useNavigate();

  const { data: dashboardData } = useQuery(
    'provider-dashboard',
    async () => {
      const response = await api.get('/provider/dashboard');
      return response.data?.data || response.data || {};
    },
    {
      refetchInterval: 30000,
      enabled: !!user && !!localStorage.getItem('token'),
    },
  );

  const { data: productsData } = useQuery(
    'provider-products',
    async () => {
      const response = await api.get('/provider/products', {
        params: { per_page: 4 },
      });
      return response.data?.data || response.data || [];
    },
    {
      enabled: !!user && !!localStorage.getItem('token'),
    },
  );

  const stats = dashboardData || {};
  const products = productsData || [];

  // Use dashboard stats for counts (more accurate from backend)
  const totalProducts = stats.total_products ?? products.length;

  return (
    <div>
      <Title level={2} className="page-title">
        Welcome back, {user?.full_name || user?.name || user?.business_name || 'Provider'}!
      </Title>

      {/* Stats Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={12} sm={6}>
          <Card>
            <Statistic
              title="Total Products"
              value={totalProducts}
              prefix={<ShoppingOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card>
            <Statistic
              title="Pending Orders"
              value={stats.pending_orders || 0}
              prefix={<ShoppingOutlined />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card>
            <Statistic
              title="Completed Orders"
              value={stats.completed_orders || 0}
              prefix={<ShoppingOutlined />}
              valueStyle={{ color: '#10b981' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card>
            <Statistic
              title="Wallet Balance"
              value={stats.wallet_balance || 0}
              prefix={<DollarOutlined />}
              valueStyle={{ color: '#722ed1' }}
              precision={2}
              formatter={(value) => `Rs. ${value}`}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[24, 24]}>
        {/* Quick Actions */}
        <Col xs={24}>
          <Card title="Quick Actions">
            <Space direction="vertical" style={{ width: '100%' }}>
              <Button
                type="primary"
                block
                size="large"
                icon={<ShoppingOutlined />}
                onClick={() => navigate('/products')}
              >
                Add Products
              </Button>
              <Button
                block
                size="large"
                icon={<DollarOutlined />}
                onClick={() => navigate('/earnings')}
              >
                View Earnings
              </Button>
            </Space>
          </Card>
        </Col>
      </Row>

      {/* Products Overview */}
      <Row gutter={[24, 24]} style={{ marginTop: 24 }}>
        <Col xs={24}>
          <Card
            title="My Products"
            extra={
              <Button type="link" onClick={() => navigate('/products')}>
                Manage
              </Button>
            }
          >
            {products.length > 0 ? (
              <Row gutter={[16, 16]}>
                {products.slice(0, 4).map((product) => (
                  <Col xs={12} sm={6} key={product.id}>
                    <Card size="small">
                      <Statistic
                        title={product.name}
                        value={product.price}
                        prefix={<DollarOutlined />}
                        valueStyle={{ fontSize: 16 }}
                        formatter={(value) => `Rs. ${value}`}
                      />
                      <Tag
                        color={product.is_available ? 'green' : 'default'}
                        style={{ marginTop: 8 }}
                      >
                        {product.is_available ? 'Active' : 'Inactive'}
                      </Tag>
                    </Card>
                  </Col>
                ))}
              </Row>
            ) : (
              <div style={{ textAlign: 'center', padding: 40 }}>
                <Text type="secondary">No products yet. Add your first product!</Text>
                <br />
                <Button
                  type="primary"
                  style={{ marginTop: 16 }}
                  onClick={() => navigate('/products')}
                >
                  Add Product
                </Button>
              </div>
            )}
          </Card>
        </Col>
      </Row>

      {/* Performance */}
      <Row gutter={[24, 24]} style={{ marginTop: 24 }}>
        <Col xs={24} lg={12}>
          <Card title="Performance">
            <Space direction="vertical" style={{ width: '100%' }}>
              <div>
                <Text strong>Completed Orders</Text>
                <Progress
                  percent={
                    stats.total_orders > 0
                      ? Math.round((stats.completed_orders / stats.total_orders) * 100)
                      : 0
                  }
                  status="active"
                  strokeColor="#10b981"
                  format={(percent) =>
                    `${stats.completed_orders || 0} / ${stats.total_orders || 0} (${percent}%)`
                  }
                />
              </div>
              <div>
                <Text strong>Average Rating</Text>
                <Progress
                  percent={(stats.average_rating || 0) * 20}
                  status="active"
                  strokeColor="#faad14"
                  format={(percent) => `${(percent / 20).toFixed(1)} ★`}
                />
              </div>
            </Space>
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title="Account Info">
            <Row gutter={16}>
              <Col span={24}>
                <Text type="secondary">Status</Text>
                <div>
                  <Tag color="green">Active</Tag>
                </div>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Dashboard;
